import { config } from '../config';

/**
 * Centralized session storage management for room-related data
 * Uses config.storagePrefix to support multiple app variants
 */
class RoomSessionStorage {
  private static readonly PREFIX = config.storagePrefix;
  
  private static getKey(key: string): string {
    return `${this.PREFIX}-${key}`;
  }

  // Room name
  static getRoomName(): string | null {
    return sessionStorage.getItem(this.getKey('roomName'));
  }

  static setRoomName(name: string): void {
    sessionStorage.setItem(this.getKey('roomName'), name);
  }

  // Player name
  static getPlayerName(): string | null {
    return sessionStorage.getItem(this.getKey('playerName'));
  }

  static setPlayerName(name: string): void {
    sessionStorage.setItem(this.getKey('playerName'), name);
  }

  // Avatar
  static getAvatar(): string | null {
    return sessionStorage.getItem(this.getKey('avatar'));
  }

  static setAvatar(avatar: string): void {
    sessionStorage.setItem(this.getKey('avatar'), avatar);
  }

  // Host status
  static getIsHost(): boolean {
    return sessionStorage.getItem(this.getKey('isHost')) === 'true';
  }

  static setIsHost(isHost: boolean): void {
    sessionStorage.setItem(this.getKey('isHost'), isHost ? 'true' : 'false');
  }

  // Host token
  static getHostToken(): string | null {
    return sessionStorage.getItem(this.getKey('hostToken'));
  }

  static setHostToken(token: string): void {
    sessionStorage.setItem(this.getKey('hostToken'), token);
  }

  // Premium room expiration date (timestamp in milliseconds)
  static getRoomExpiresAt(): number | null {
    const stored = sessionStorage.getItem(this.getKey('roomExpiresAt'));
    return stored ? parseInt(stored, 10) : null;
  }

  static setRoomExpiresAt(expiresAt: number): void {
    sessionStorage.setItem(this.getKey('roomExpiresAt'), expiresAt.toString());
  }

  static clearRoomExpiresAt(): void {
    sessionStorage.removeItem(this.getKey('roomExpiresAt'));
  }

  // Check if room has expired
  static isRoomExpired(): boolean {
    const expiresAt = this.getRoomExpiresAt();
    if (!expiresAt) {
      return false; // No expiration set, assume not expired
    }
    return Date.now() > expiresAt;
  }

  // Category votes (stored per game type and room)
  static getCategoryVotes(roomName: string, gameType: string): string[] {
    const key = this.getKey(`categoryVotes-${roomName}-${gameType}`);
    const stored = sessionStorage.getItem(key);
    return stored ? JSON.parse(stored) : [];
  }

  static setCategoryVotes(roomName: string, gameType: string, votes: string[]): void {
    const key = this.getKey(`categoryVotes-${roomName}-${gameType}`);
    sessionStorage.setItem(key, JSON.stringify(votes));
  }

  static clearCategoryVotes(roomName: string, gameType: string): void {
    const key = this.getKey(`categoryVotes-${roomName}-${gameType}`);
    sessionStorage.removeItem(key);
  }

  // Get all saved room data at once
  static getSavedRoomData(): {
    roomName: string | null;
    playerName: string | null;
    avatar: string | null;
    isHost: boolean;
    hostToken: string | null;
    expiresAt: number | null;
  } {
    return {
      roomName: this.getRoomName(),
      playerName: this.getPlayerName(),
      avatar: this.getAvatar(),
      isHost: this.getIsHost(),
      hostToken: this.getHostToken(),
      expiresAt: this.getRoomExpiresAt(),
    };
  }

  // Clear all room-related session storage
  static clearAll(): void {
    const keys = ['roomName', 'playerName', 'avatar', 'isHost', 'hostToken', 'roomExpiresAt'];
    keys.forEach(key => {
      sessionStorage.removeItem(this.getKey(key));
    });
    // Also clear all category votes (they use dynamic keys)
    const roomName = this.getRoomName();
    if (roomName) {
      // Clear category votes for all possible game types
      const gameTypes = ['trivia', 'picture-perfect', 'close-or-not', 'word-puzzle', 'speed-challenge', 'fact-or-myth'];
      gameTypes.forEach(gameType => {
        this.clearCategoryVotes(roomName, gameType);
      });
    }
  }

  // Clear specific items
  static clearRoomData(): void {
    sessionStorage.removeItem(this.getKey('roomName'));
  }

  /**
   * Clear room-related data but keep player name and avatar.
   * Use when join fails (e.g. room not found) so reconnection doesn't retry this room,
   * but the user doesn't have to re-enter their name when joining another room.
   */
  static clearRoomKeepPlayer(): void {
    this.clearRoomData();
    this.clearRoomExpiresAt();
    this.clearHostData();
  }

  static clearPlayerData(): void {
    sessionStorage.removeItem(this.getKey('playerName'));
    sessionStorage.removeItem(this.getKey('avatar'));
  }

  static clearHostData(): void {
    sessionStorage.removeItem(this.getKey('isHost'));
    sessionStorage.removeItem(this.getKey('hostToken'));
  }
}

export default RoomSessionStorage;
